import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/primitives/button";
import { cn } from "@/lib/utils";
import { applyMarkdownFormat } from "@/lib/markdown";
import type { MarkdownFormat } from "@/lib/markdown";
import {
  TOOLBAR_PRIMARY,
  TOOLBAR_OVERFLOW,
  type FormattingOption,
} from "@/config/formattingConfig";
import { Check, ArrowLeft, Ellipsis } from "lucide-react";
import { useTranslation } from "react-i18next";

interface MobileFormattingBubbleProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (newValue: string) => void;
}

export default function MobileFormattingBubble({
  textareaRef,
  value,
  onChange,
}: MobileFormattingBubbleProps) {
  const { t } = useTranslation();
  const [hasSelection, setHasSelection] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [lastApplied, setLastApplied] = useState<MarkdownFormat | null>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);
  const savedSelectionRef = useRef({ start: 0, end: 0 });
  const bubbleRef = useRef<HTMLDivElement>(null);

  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Track whether the textarea has a non-collapsed selection.
  useEffect(() => {
    const check = () => {
      const ta = textareaRef.current;
      if (
        !ta ||
        (document.activeElement !== ta &&
          !bubbleRef.current?.contains(document.activeElement))
      ) {
        setHasSelection(false);
        return;
      }
      setHasSelection(ta.selectionStart !== ta.selectionEnd);
    };

    document.addEventListener("selectionchange", check);
    return () => document.removeEventListener("selectionchange", check);
  }, [textareaRef]);

  // Reset overflow / link panels when bubble hides.
  useEffect(() => {
    if (!hasSelection) {
      setShowOverflow(false);
      setIsLinkOpen(false);
      setLinkUrl("");
    }
  }, [hasSelection]);

  const applyFormat = useCallback(
    (format: MarkdownFormat) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }

      if (format === "link") {
        savedSelectionRef.current = {
          start: textarea.selectionStart,
          end: textarea.selectionEnd,
        };
        setIsLinkOpen(true);
        setTimeout(() => linkInputRef.current?.focus(), 0);
        return;
      }

      const { selectionStart, selectionEnd } = textarea;
      const result = applyMarkdownFormat(
        valueRef.current,
        selectionStart,
        selectionEnd,
        format,
      );

      onChangeRef.current(result.newText);
      setLastApplied(format);
      setTimeout(() => setLastApplied(null), 200);

      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(
          result.newSelectionStart,
          result.newSelectionEnd,
        );
      });
    },
    [textareaRef],
  );

  const handleLinkConfirm = useCallback(() => {
    const url = linkUrl.trim();
    if (!url) {
      return;
    }
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    const { start, end } = savedSelectionRef.current;
    const current = valueRef.current;
    const before = current.slice(0, start);
    const selected = current.slice(start, end);
    const after = current.slice(end);
    const linkText = selected || "link";
    const newText = `${before}[${linkText}](${url})${after}`;
    const newEnd = start + `[${linkText}](${url})`.length;

    onChangeRef.current(newText);
    setIsLinkOpen(false);
    setLinkUrl("");
    setLastApplied("link");
    setTimeout(() => setLastApplied(null), 200);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(newEnd, newEnd);
    });
  }, [linkUrl, textareaRef]);

  const handleLinkBack = useCallback(() => {
    setIsLinkOpen(false);
    setLinkUrl("");
    textareaRef.current?.focus();
  }, [textareaRef]);

  const renderButton = (opt: FormattingOption) => {
    const Icon = opt.icon;
    return (
      <Button
        key={opt.key}
        type="button"
        size="icon"
        variant="ghost"
        className={cn(
          "size-8 rounded-none hover:bg-accent hover:text-accent-foreground",
          lastApplied === opt.key && "bg-accent text-accent-foreground",
        )}
        aria-label={t(opt.labelKey)}
        onClick={() => applyFormat(opt.key)}
      >
        <Icon size={16} />
      </Button>
    );
  };

  if (!hasSelection) {
    return null;
  }

  const visibleFormats = showOverflow ? TOOLBAR_OVERFLOW : TOOLBAR_PRIMARY;

  return (
    <div
      ref={bubbleRef}
      className="absolute -top-11 left-0 z-50 flex items-center rounded-lg border border-border bg-popover shadow-md"
      onMouseDown={(e) => {
        // Prevent stealing focus from textarea on all non-input taps.
        if ((e.target as HTMLElement).tagName !== "INPUT") {
          e.preventDefault();
        }
      }}
    >
      {isLinkOpen ? (
        <div className="flex items-center">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-8 shrink-0"
            aria-label={t("editor.formattingOptions")}
            onClick={handleLinkBack}
          >
            <ArrowLeft size={16} />
          </Button>
          <div className="mx-0.5 h-5 w-px shrink-0 bg-border" />
          <input
            ref={linkInputRef}
            className="w-36 bg-transparent px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            type="text"
            value={linkUrl}
            placeholder={t("editor.link")}
            autoComplete="off"
            inputMode="url"
            dir="auto"
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && linkUrl.trim()) {
                e.preventDefault();
                handleLinkConfirm();
              }
              if (e.key === "Escape") {
                handleLinkBack();
              }
            }}
          />
          {linkUrl.trim() && (
            <>
              <div className="mx-0.5 h-5 w-px shrink-0 bg-border" />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-8 shrink-0 text-primary"
                aria-label={t("editor.link")}
                onClick={handleLinkConfirm}
              >
                <Check size={16} />
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center divide-x divide-border">
          {showOverflow && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-8 rounded-none hover:bg-accent hover:text-accent-foreground"
              aria-label={t("editor.formattingOptions")}
              onClick={() => setShowOverflow(false)}
            >
              <ArrowLeft size={16} />
            </Button>
          )}
          {visibleFormats.map(renderButton)}
          {!showOverflow && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-8 rounded-none hover:bg-accent hover:text-accent-foreground"
              aria-label={t("editor.more")}
              onClick={() => setShowOverflow(true)}
            >
              <Ellipsis size={16} />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
